import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SharedModule } from "src/shared/shared.module";
import { VideoPlayerComponent } from "./components/video-player/video-player.component";
import { PipesModule } from "../../../../utils/pipes/pipes.module";
import { environment } from "src/environments/environment";
import { PageInfoService } from "src/app/_metronic/layout";
import { forkJoin, lastValueFrom, Observable } from "rxjs";
import { StudentCourseService } from "src/app/services/api/student-course.service";
import { StudentCourse } from "src/app/services/model/student-course.model";
import {
  Course,
  CourseChapter,
  CourseChapterGroup,
} from "src/app/services/model/course.model";
import { AuthService } from "../../auth";
import { CommonModule } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import { StartVideoDialogComponent } from "./components/_dialogs/start-video/start-video.component";
import { StudentCourseLatest } from "src/app/services/model/student.model";
import { ToastService } from "src/app/services/toast-service/toast.service";
import { PausedVideoComponent } from "./components/_dialogs/paused-video/paused-video.component";
import { CourseQuizListComponent } from "src/app/pages/quiz/components/course-quiz-list/course-quiz-list.component";
import { CourseFileItemComponent } from "./components/course-file-item/course-file-item.component";
import { ExpireVideoComponent } from "./components/_dialogs/expire-video/expire-video.component";
import { ReviewComponent } from "./components/_dialogs/review/review.component";
import { SkeletonWatchComponent } from "./components/skeleton-watch/skeleton-watch.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-student-watch",
  imports: [
    SharedModule,
    VideoPlayerComponent,
    PipesModule,
    CommonModule,
    PausedVideoComponent,
    CourseQuizListComponent,
    CourseFileItemComponent,
    SkeletonWatchComponent,
    TranslateModule,
  ],
  templateUrl: "./student-watch.component.html",
  styleUrl: "./student-watch.component.scss",
})
export class StudentWatchComponent implements OnInit, OnDestroy {
  @ViewChild(VideoPlayerComponent) videoPlayer!: VideoPlayerComponent;

  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #studentCourseService = inject(StudentCourseService);
  #pageInfoService = inject(PageInfoService);
  #authService = inject(AuthService);
  #dialog = inject(MatDialog);
  #toastService = inject(ToastService);
  #translate = inject(TranslateService);

  fileUrl = environment.apiFileUrl;

  student: any;

  studentCourse: any;
  courseChapterGroup = signal<CourseChapterGroup[]>([]);
  courseLasted: any;
  course: any;
  student_course_id = signal<string | undefined>(undefined);

  currentChapterSrc: { url: string; time: number } = { url: "", time: 0 };
  currentChapter: CourseChapter;
  chapter_id: number | null = null;

  courseFiles: any = [];

  isLoading = signal(true);
  isPlaying = signal(true);
  firstInit = signal(true);
  timerInterval: any;
  config = {
    forward: 5,
    back: 5,
    SYNC_DURATION: 20,
    // PERCENT_REVIEW: 20,
    PERCENT_REVIEW: 20,
  };
  timer = 0;

  handleVisibilityChange = () => {
    // this.videoPlayer?.disposeVideo();
    // if (document.hidden && this.timerInterval) {
    //   window.scrollTo(0, 0);
    //   this.clearProcess(true);
    // }
  };
  @HostListener("window:beforeunload", ["$event"])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (this.timerInterval) {
      event.preventDefault();
      (event as any).returnValue = "";
    }
  }

  constructor() {
    this.#route.params.subscribe((params) => {
      this.student_course_id.set(params["id"]);
    });
  }

  async ngOnInit(): Promise<void> {
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    this.student = this.#authService.currentUser()?.user.data;
    this.fetchData();
  }

  ngOnDestroy() {
    // `videoPlayer` is a ViewChild that only exists once the player has
    // mounted. Leaving this screen before it does — which is exactly what
    // happens when the course turns out not to be yours — threw here, and a
    // throw in ngOnDestroy aborts the router's deactivation: the redirect was
    // issued, rejected, and the browser stayed on the dead URL.
    if (this.videoPlayer && !this.videoPlayer.getIsDisposed()) {
      this.clearProcess();
    }
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    );
    window.scrollTo(0, 0);
    // this.clearProcess();
  }

  _setLoading(value: boolean, time: number = 500) {
    setTimeout(() => {
      this.isLoading.update((_) => value);
    }, time);
  }

  async fetchData() {
    forkJoin([
      this.#studentCourseService.getStudentCourseById(
        +this.student_course_id()!,
      ) as Observable<any>,
      this.#studentCourseService.getStudentCourseLasted() as Observable<any>,
    ]).subscribe({
      next: async ([student_course, latest_course]) => {
        // 200 with a null body is how the API says "not yours" here, so the
        // `error` branch below never fires on an id this account is not
        // enrolled on — the player mounted over nothing and threw twice.
        if (!student_course?.course) {
          this.notEnrolled();
          return;
        }

        this.setBreadCrumb(student_course.course.name);
        // ---------------------------------//
        this.studentCourse = student_course;
        this.courseLasted = latest_course;
        this.course = student_course.course;
        // ---------------------------------//
        await this.fetchRelationData();
        // ---------------------------------//
        this.setActiveCourseChapterGroup();
        this.setLatestCourseChapterGroup();
        // ---------------------------------//
        this.getStarted();
      },
      // A course this account is not enrolled on used to render a full player
      // shell over nothing — "0 บทเรียน · 0 วิดีโอ · สอนโดย -" with a live
      // "พักการเรียนชั่วคราว" button on it. eazycal guards the equivalent route
      // outright; go back to the classroom and say why.
      error: () => this.notEnrolled(),
      complete: () => {
        this._setLoading(false);
      },
    });
  }

  /**
   * Back to the classroom, saying why.
   *
   * eazycal guards the equivalent route outright. Here the check has to happen
   * after the response, because "not yours" arrives as a 200 with no body
   * rather than as an error.
   */
  private notEnrolled(): void {
    this._setLoading(false);
    this.#toastService.toastWarning(
      this.#translate.instant("ONLINE_CLASS.NOT_ENROLLED")
    );
    // Deferred a tick: the response lands while the router is still finishing
    // the navigation that activated this component, and a `navigate` issued
    // inside that window is dropped — the page stayed put, silently, with an
    // empty player on it.
    setTimeout(() => this.#router.navigate(["/online-class"]), 0);
  }

  async fetchRelationData() {
    this.courseFiles = await lastValueFrom(
      this.#studentCourseService.getCourseFiles({
        course_id: this.course.id,
      }) as Observable<any>,
    );
  }

  getStarted(): void {
    const dialogRef = this.#dialog.open(StartVideoDialogComponent, {
      width: "700px",
      disableClose: true,
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        // this.initTimer();
        this.onReview();
      }
    });
  }

  /**
   * In a trial ("ทดลองเรียน") enrolment only the preview videos are openable;
   * the rest stay visible but locked. Paid enrolments lock nothing.
   *
   * The API enforces this too — the trial quota only covers the preview
   * chapters — but without the check here the player opens a chapter it cannot
   * stream and the student watches it fail.
   */
  isChapterLocked(item: CourseChapter): boolean {
    return !!this.studentCourse?.is_trial && !item?.is_preview;
  }

  async onSelectedChapter(payload: {
    data: CourseChapter;
    playlist?: CourseChapterGroup;
    currentTime?: number;
  }) {
    if (this.isChapterLocked(payload.data)) {
      this.#toastService.toastWarning(
        this.#translate.instant("ONLINE_CLASS.TRIAL_LOCKED"),
      );
      return;
    }

    if (this.isNewChapter(payload)) {
      this.updateProgression();
      //----------------------------//
      const chapter = payload.data.chapter;
      const durationVideo = chapter.duration_video;
      // A chapter watched to the end must start over, not open a frame from
      // the credits. Clamping to the duration (what this used to do) leaves the
      // student staring at a finished video with nothing to play — the defect
      // eazycal fixed in dfd86a3.
      //
      // The guard has to sit AFTER the caller's own resume position, not just
      // the stored one: the chapter list passes chapter.current_time straight
      // in, so checking only the stored value let the finished case through.
      const requested = payload.currentTime ?? chapter.current_time ?? 0;
      let calculateCurrentTime = requested >= durationVideo ? 0 : requested;
      //----------------------------//
      this.currentChapter = payload.data;
      this.currentChapterSrc = {
        url: `${this.fileUrl}${chapter.has_hls ? chapter.hls_url : chapter.video_url || ""
          }`,
        time: calculateCurrentTime,
      };
    }
  }

  /**
   * The clip finished — roll on to the next one, the way eazycal does.
   *
   * Stops at the end of the course, and stops at the first chapter a trial
   * locks: silently, because the student did not ask for it and a warning toast
   * for something they did not click is noise.
   */
  onVideoEnded(): void {
    const ordered: { chapter: CourseChapter; group: CourseChapterGroup }[] = [];
    this.courseChapterGroup().forEach((group: CourseChapterGroup) =>
      (group.course_chapters || []).forEach((chapter: CourseChapter) =>
        ordered.push({ chapter, group }),
      ),
    );

    const at = ordered.findIndex(
      (item) => item.chapter.chapter_id === this.currentChapter?.chapter_id,
    );
    const next = at >= 0 ? ordered[at + 1] : undefined;
    if (!next || this.isChapterLocked(next.chapter)) {
      return;
    }

    this.onSelectedChapter({
      data: next.chapter,
      playlist: next.group,
      currentTime: 0,
    });
  }

  setActiveCourseChapterGroup(): void {
    this.courseChapterGroup.set(
      this.studentCourse.course.course_chapter_groups.map(
        (group: CourseChapterGroup) => {
          return {
            ...group,
            active: false,
            course_chapters: group.course_chapters.map(
              (chapter: CourseChapter) => {
                // Find current Time by Chapter ID
                const studentTime =
                  this.studentCourse.student_chapter_current_times.find(
                    (time: any) =>
                      time.chapter_id === chapter.chapter.id &&
                      time.course_chapter_group_id ===
                      chapter.course_chapter_group_id,
                  );
                const currentTime = studentTime ? studentTime.current_time : 0;
                const duration = chapter.chapter.duration_video || 1;
                // Patch Current Time in Chapter
                return {
                  ...chapter,
                  chapter: {
                    ...chapter.chapter,
                    current_time: currentTime,
                    progress: (currentTime / duration) * 100,
                  },
                };
              },
            ),
          };
        },
      ),
    );
  }

  setLatestCourseChapterGroup(): void {
    // find Group Chapter Index for Last Watch and Set Active
    const latestChapterGroupIndex = !this.isNoCourseLated()
      ? this.courseChapterGroup().findIndex(
        (item: any) => item.id === this.courseLasted.course_chapter_group_id,
      )
      : 0;
    this.courseChapterGroup()[latestChapterGroupIndex].active = true;
    // find Chapter Index for Last Watch and Set Active
    const latestChapterIndex = !this.isNoCourseLated()
      ? this.courseChapterGroup()[
        latestChapterGroupIndex
      ].course_chapters.findIndex(
        (item: any) => item.id === this.courseLasted.course_chapter_id,
      )
      : 0;
    for (let i = 0; i <= this.courseChapterGroup().length - 1; i++) {
      this.isExpanded().push(false);
    }
    this.toggleCollapse(latestChapterGroupIndex);
    //----------------------------//
    // Every other read of courseLasted here is guarded by isNoCourseLated(),
    // this one was not. Opening a course you have never watched leaves
    // courseLasted null (or pointing at a DIFFERENT enrolment), so this threw
    // and took the whole chapter selection down with it — the player then sat
    // empty with no clip loaded. When there is no history for THIS enrolment
    // the answer is the start of the first chapter.
    this.onSelectedChapter({
      data: this.courseChapterGroup()[latestChapterGroupIndex].course_chapters[
        latestChapterIndex
      ],
      playlist: this.courseChapterGroup()[latestChapterGroupIndex],
      currentTime: this.isNoCourseLated() ? 0 : this.courseLasted.current_time,
    });
  }

  async updateProgression() {
    await this.updateCourseLatestInChapter();
    this.studentCourse = await this.#studentCourseService
      .getStudentCourseById(+this.student_course_id()!)
      .toPromise();
    const latestChapterGroupIndex = this.courseChapterGroup().findIndex(
      (item: CourseChapterGroup) => item.active === true,
    );
    this.setActiveCourseChapterGroup();
    if (latestChapterGroupIndex >= 0) {
      this.courseChapterGroup()[latestChapterGroupIndex]!.active = true;
    }
  }

  async updateCourseLatest(): Promise<void> {
    if (!this.currentChapterSrc!.url && !this.currentChapter?.id) {
      return;
    }
    await this.updateLatestService();
  }
  async updateCourseLatestInChapter() {
    if (!this.student || !this.currentChapter) {
      return;
    }
    await this.updateLatestService();
  }
  async updateLatestService() {
    const payload: StudentCourseLatest = {
      current_time: this.videoPlayer?.getCurrentTime()!,
      course_id: this.course.id,
      student_id: this.student.id,
      student_course_id: this.studentCourse.id,
      course_chapter_id: this.currentChapter.id,
      course_chapter_group_id: this.currentChapter.course_chapter_group_id,
    };
    await this.#studentCourseService.updateCourseLatest(payload).toPromise();
  }

  //--------------Start Counting Time--------------//
  initTimer(): void {
    if (this.timerInterval) {
      return;
    }
    this.timer = 0;
    this.timerInterval = setInterval(() => {
      if (this.isExpireTime()) {
        this.onExpire();
        return;
      }
      if (!this.isTimeSync()) {
        this.timer++;
      }
      this.syncData();
    }, 1000);
  }

  //-------------Update Time Spent To API---------------//
  syncData(): void {
    if (this.isTimeSync()) {
      this.timer = 0;
      this.updateTimeSpent(this.config.SYNC_DURATION).subscribe(
        async (res: StudentCourse) => {
          this.studentCourse = res;
          this.updateCourseLatest();
        },
        async (error) => {
          clearInterval(this.timerInterval);
          this.#toastService.toastError(
            "เกิดข้อผิดพลาด: " + error.error.errors,
          );
        },
      );
    }
  }
  updateTimeSpent(
    timer: number,
    isPlaying?: boolean,
  ): Observable<StudentCourse> {
    const payload = {
      id: this.studentCourse.id!,
      time_spent: timer,
      video_time: this.videoPlayer?.getCurrentTime(),
      is_playing: isPlaying ? isPlaying : this.videoPlayer.getIsPlaying(),
      course_chapter_id: this.currentChapter?.id!,
    };

    return this.#studentCourseService.updateTimeSpent(payload);
  }

  onExpire(): void {
    this.updateTimeSpent(this.timer).subscribe(
      (res) => {
        if (res) {
          this.onPauseCourse();
          this.onReview();
          this.expire();
        }
      },
      async (err) => {
        this.#toastService.toastError("เกิดข้อผิดพลาด: " + err.error.errors);
        // await this.swalService.swalOnError('', true);
        // this.location.back();
      },
    );
  }

  expire(): void {
    const dialogRef = this.#dialog.open(ExpireVideoComponent, {
      width: "100vw",
      height: "100vh",
      maxWidth: "100vw",
      data: {},
      disableClose: true,
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.#router.navigateByUrl("/online-class");
      }
    });
  }

  onPauseCourse(is_reset = true) {
    // Logic to pause the course
    if (this.videoPlayer) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
      this.updateCourseLatest();
      if (is_reset) {
        this.videoPlayer?.onPause()!;
        this.isPlaying.update((_) => false);
      } else if (!is_reset && !this.firstInit()) {
        this.updateTimer();
      }
    }
  }
  onResume() {
    if (this.videoPlayer) {
      this.initTimer();
      this.videoPlayer?.onResume()!;
      this.isPlaying.update((_) => true);
    }
  }

  onReview(): void {
    if (this.isReview()) {
      this.onPauseCourse();
      this.review();
    }
  }
  review(): void {
    const dialogRef = this.#dialog.open(ReviewComponent, {
      width: "610px",
      disableClose: true,
      autoFocus: false,
      data: this.studentCourse,
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.onResume();
      }
    });
  }

  isExpireTime(): boolean {
    return (
      this.studentCourse.duration_spent + this.timer >=
      this.studentCourse.expire_duration_video
    );
  }

  isTimeSync(): boolean {
    return this.timer >= this.config.SYNC_DURATION;
  }

  isNoCourseLated() {
    return (
      !this.courseLasted ||
      this.courseLasted.student_course_id !== this.studentCourse.id
    );
  }

  isNewChapter(payload: {
    data: CourseChapter;
    currentTime?: number;
    playlist?: CourseChapterGroup;
  }) {
    return (
      !this.currentChapter ||
      payload.data.chapter_id !== this.currentChapter?.chapter_id ||
      (payload.data.chapter_id === this.currentChapter?.chapter_id &&
        payload.data.course_chapter_group_id !==
        this.currentChapter?.course_chapter_group_id)
    );
  }

  isReview(): boolean {
    return (
      !this.studentCourse.is_reviewed &&
      this.studentCourse.progress > this.config.PERCENT_REVIEW
    );
  }

  clearProcess(isPaused: boolean = false): void {
    this.onPauseCourse();
    this.updateTimer();
    // if (!isPaused) {
    // }
  }
  updateTimer() {
    clearInterval(this.timerInterval);
    // this.updateCourseLatest();
    this.updateTimeSpent(this.timer, false).subscribe();
  }

  isExpanded = signal<boolean[]>([]);
  toggleCollapse(index: number): void {
    let rawExpanded = this.isExpanded();
    rawExpanded.map((item: boolean, i: number) => {
      rawExpanded[i] = i === index ? (item === true ? false : true) : false;
    });
    this.isExpanded.update((_) => rawExpanded);
  }

  videoListener(event: { is_playing: boolean }) {
    const playing = event.is_playing;
    if (playing && !this.firstInit()) {
      this.initTimer();
      // console.log("play");
    } else if (!playing) {
      // console.log("paused");
      this.onPauseCourse(false);
    }
    if (this.firstInit()) {
      this.firstInit.set(false);
    }

    // this.watchVideoComponent.videoListener()
  }

  setBreadCrumb(name: string) {
    this.#pageInfoService.updateTitle(name);
  }
}
